import { Info, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { formatDateValue, formatNumber, toNumber } from '@/lib/utils';

import { useAnularToma, useCrearToma, useEditarToma } from '../hooks/use-ric';
import { aFechaHoraApi, fechaHoraAhora } from '../lib/ric-format';
import type { TanqueRic, TomaRic } from '../ric.types';

/** Existencia teórica guardada de una toma. */
function tomarLogica(toma: TomaRic): number {
  return toNumber(toma.ric_previous) + toNumber(toma.ric_incomes) - toNumber(toma.ric_outcomes);
}

interface TomaDialogProps {
  onClose: () => void;
  tanques: TanqueRic[];
  /** Tanque preseleccionado (el de la consulta actual). */
  tanqueInicial: number;
  /** Última toma activa del tanque, para mostrar la existencia previa. */
  ultima: { ric_date: string; ric_physical?: number } | undefined;
  onCreada: (mensaje: string) => void;
}

/**
 * Captura de una toma física.
 *
 * Sólo se piden los datos del varillaje: la existencia previa, los ingresos, las
 * salidas y la diferencia las calcula el backend con la operación registrada
 * (igual que `API/Tanks::measurement_put`).
 */
export function TomaDialog({
  onClose,
  tanques,
  tanqueInicial,
  ultima,
  onCreada,
}: TomaDialogProps) {
  const crear = useCrearToma();

  const [tank, setTank] = useState(String(tanqueInicial));
  const [fechaHora, setFechaHora] = useState(() => fechaHoraAhora());
  const [galonaje, setGalonaje] = useState('');
  const [varillaje, setVarillaje] = useState('');
  const [agua, setAgua] = useState('0');
  const [recibido, setRecibido] = useState('0');
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const enviar = () => {
    const encontrados: Record<string, string> = {};
    const galones = toNumber(galonaje);

    if (!tank) encontrados.tank = 'Selecciona el tanque.';
    if (!fechaHora) encontrados.fechaHora = 'Indica la fecha y hora de la toma.';
    if (!(galones >= 0)) encontrados.galonaje = 'El galonaje físico no puede ser negativo.';

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) {
      setAviso('Revisa los campos marcados.');
      return;
    }

    crear.mutate(
      {
        tank: Number(tank),
        datetime: aFechaHoraApi(fechaHora),
        gallonage: galones,
        height: toNumber(varillaje),
        water_volume: toNumber(agua),
        received_tanks: toNumber(recibido),
      },
      {
        onSuccess: () => {
          onCreada('Toma registrada correctamente.');
          onClose();
        },
        onError: (error) => setAviso(error.message || 'No se pudo registrar la toma.'),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Capturar toma física"
      description="Registra el varillaje; el sistema calcula la existencia teórica y la diferencia."
      size="md"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={crear.isPending}>
            Cancelar
          </Button>
          <Button size="sm" loading={crear.isPending} onClick={enviar}>
            Guardar toma
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? (
          <Alert variant="error" title="No se pudo guardar">
            {aviso}
          </Alert>
        ) : null}

        {ultima ? (
          <div className="rounded-control border border-brand-100 bg-brand-50/60 px-3 py-2 text-xs text-brand-900">
            <p className="flex items-center gap-1.5 font-semibold">
              <Info className="size-3.5" aria-hidden="true" />
              Existencia previa del tanque
            </p>
            <p className="mt-1">
              {ultima.ric_physical !== undefined && ultima.ric_physical !== null
                ? `${formatNumber(ultima.ric_physical)} gal`
                : 'Sin registro previo'}
              <span className="text-brand-700">
                {' '}
                · última toma del {formatDateValue(ultima.ric_date)}
              </span>
            </p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="text-sm font-medium text-ink-700">Tanque</span>
            <Select
              value={tank}
              onChange={(event) => setTank(event.target.value)}
              className="h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            >
              {tanques.map((opcion) => (
                <option key={opcion.id_tank} value={String(opcion.id_tank)}>
                  Tanque {opcion.id_tank}
                  {opcion.producto ? ` · ${opcion.producto}` : ''}
                </option>
              ))}
            </Select>
            {errores.tank ? <span className="text-xs text-rose-600">{errores.tank}</span> : null}
          </label>

          <Input
            containerClassName="sm:col-span-2"
            label="Fecha y hora de la toma"
            type="datetime-local"
            value={fechaHora}
            error={errores.fechaHora}
            hint="El día operativo es la fecha de la toma menos un día."
            onChange={(event) => setFechaHora(event.target.value)}
          />

          <Input
            label="Galonaje físico"
            type="number"
            min={0}
            step="0.001"
            inputMode="decimal"
            value={galonaje}
            error={errores.galonaje}
            onChange={(event) => setGalonaje(event.target.value)}
          />

          <Input
            label="Varillaje (mm)"
            type="number"
            min={0}
            step="0.1"
            inputMode="decimal"
            value={varillaje}
            onChange={(event) => setVarillaje(event.target.value)}
          />

          <Input
            label="Agua (gal)"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={agua}
            onChange={(event) => setAgua(event.target.value)}
          />

          <Input
            label="Recibido en tanque (gal)"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={recibido}
            hint="Volumen recibido declarado en la factura."
            onChange={(event) => setRecibido(event.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

interface EditarTomaDialogProps {
  toma: TomaRic;
  onClose: () => void;
  onGuardado: (mensaje: string) => void;
}

/** Edición de una toma activa: la existencia teórica no cambia, la diferencia sí. */
export function EditarTomaDialog({ toma, onClose, onGuardado }: EditarTomaDialogProps) {
  const editar = useEditarToma();

  const [galonaje, setGalonaje] = useState(String(toma.ric_physical));
  const [varillaje, setVarillaje] = useState(String(toma.ric_dipstick));
  const [agua, setAgua] = useState(String(toma.ric_water_volume));
  const [recibido, setRecibido] = useState(String(toma.ric_received_tanks));
  const [comentarios, setComentarios] = useState(toma.ric_comments ?? '');
  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const logica = tomarLogica(toma);

  const enviar = () => {
    if (!motivo.trim()) {
      setAviso('El motivo de la edición es obligatorio.');
      return;
    }

    editar.mutate(
      {
        id: toma.ric_id,
        payload: {
          reason: motivo.trim(),
          gallonage: toNumber(galonaje),
          height: toNumber(varillaje),
          water_volume: toNumber(agua),
          received_tanks: toNumber(recibido),
          comments: comentarios.trim(),
        },
      },
      {
        onSuccess: () => {
          onGuardado('Registro editado correctamente.');
          onClose();
        },
        onError: (error) => setAviso(error.message || 'No se pudo editar el registro.'),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Editar toma #${toma.ric_id}`}
      description="Se guarda el motivo del cambio para la trazabilidad del inventario."
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={editar.isPending}>
            Cancelar
          </Button>
          <Button size="sm" loading={editar.isPending} onClick={enviar}>
            Guardar cambios
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? (
          <Alert variant="error" title="Revisa el dato">
            {aviso}
          </Alert>
        ) : null}

        <div className="grid gap-2 rounded-control bg-ink-50/70 px-3 py-2 text-xs text-ink-600 sm:grid-cols-3">
          <span>Existencia previa: {formatNumber(toma.ric_previous)} gal</span>
          <span>Ingresos: {formatNumber(toma.ric_incomes)} gal</span>
          <span>Salidas: {formatNumber(toma.ric_outcomes)} gal</span>
          <span className="sm:col-span-3">
            Existencia teórica: {formatNumber(logica)} gal · diferencia actual:{' '}
            {formatNumber(toma.ric_difference)} gal
          </span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Galonaje físico"
            type="number"
            min={0}
            step="0.001"
            inputMode="decimal"
            value={galonaje}
            onChange={(event) => setGalonaje(event.target.value)}
          />

          <Input
            label="Varillaje (mm)"
            type="number"
            min={0}
            step="0.1"
            inputMode="decimal"
            value={varillaje}
            onChange={(event) => setVarillaje(event.target.value)}
          />

          <Input
            label="Agua (gal)"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={agua}
            onChange={(event) => setAgua(event.target.value)}
          />

          <Input
            label="Recibido en tanque (gal)"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={recibido}
            onChange={(event) => setRecibido(event.target.value)}
          />

          <Input
            containerClassName="sm:col-span-2"
            label="Comentarios"
            value={comentarios}
            onChange={(event) => setComentarios(event.target.value)}
          />

          <Input
            containerClassName="sm:col-span-2"
            label="Motivo de la edición"
            value={motivo}
            error={
              aviso && !motivo.trim() ? 'Obligatorio para dejar constancia del cambio.' : undefined
            }
            placeholder="Ej. Corrección del varillaje por error de lectura"
            onChange={(event) => setMotivo(event.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}

interface AnularTomaDialogProps {
  toma: TomaRic;
  onClose: () => void;
  onAnulada: (mensaje: string) => void;
}

/** Anulación de una toma: no se borra, queda con el motivo y el usuario. */
export function AnularTomaDialog({ toma, onClose, onAnulada }: AnularTomaDialogProps) {
  const anular = useAnularToma();

  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const enviar = () => {
    if (!motivo.trim()) {
      setAviso('El motivo de la anulación es obligatorio.');
      return;
    }

    anular.mutate(
      { id: toma.ric_id, reason: motivo.trim() },
      {
        onSuccess: () => {
          onAnulada('Registro anulado correctamente.');
          onClose();
        },
        onError: (error) => setAviso(error.message || 'No se pudo anular el registro.'),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Anular toma #${toma.ric_id}`}
      description="El registro se conserva en la pestaña de anulados con el motivo indicado."
      size="sm"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={anular.isPending}>
            Cancelar
          </Button>
          <Button variant="danger" size="sm" loading={anular.isPending} onClick={enviar}>
            Anular registro
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {aviso ? (
          <Alert variant="error" title="Revisa el dato">
            {aviso}
          </Alert>
        ) : null}

        <p className="flex items-start gap-2 text-sm text-ink-600">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-500" aria-hidden="true" />
          Anular una toma no la elimina: queda registrada con su motivo para auditoría. Tanque{' '}
          {toma.ric_tank} · {formatDateValue(toma.ric_date)}.
        </p>

        <Input
          label="Motivo de la anulación"
          value={motivo}
          placeholder="Ej. Toma registrada en el tanque equivocado"
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>
    </Dialog>
  );
}

interface DetalleTomaDialogProps {
  toma: TomaRic;
  umbral: number;
  onClose: () => void;
}

/** Detalle del cálculo de una toma (la fórmula paso a paso del panel anterior). */
export function DetalleTomaDialog({ toma, umbral, onClose }: DetalleTomaDialogProps) {
  const logica = tomarLogica(toma);
  const sinAgua = toNumber(toma.ric_physical) - toNumber(toma.ric_water_volume);
  const diferencia = sinAgua - logica;
  const fueraDeUmbral = Math.abs(diferencia) > umbral;

  const filas: { label: string; valor: number; nota?: string }[] = [
    { label: 'Existencia previa', valor: toma.ric_previous },
    { label: 'Ingresos del período', valor: toma.ric_incomes },
    {
      label: 'Salidas del período',
      valor: toma.ric_outcomes,
      nota: 'Incluye mermas, calibraciones y otras salidas',
    },
    { label: 'Existencia teórica', valor: logica, nota: 'Previo + ingresos − salidas' },
    { label: 'Varillaje (altura)', valor: toma.ric_dipstick },
    { label: 'Agua en el tanque', valor: toma.ric_water_volume },
    { label: 'Volumen físico sin agua', valor: sinAgua, nota: 'Físico − agua' },
    { label: 'Diferencia', valor: diferencia, nota: 'Físico sin agua − teórica' },
    { label: 'Retornos a tanque', valor: toma.ric_returns },
    { label: 'Recibido en tanque', valor: toma.ric_received_tanks },
  ];

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Toma #${toma.ric_id} · Tanque ${toma.ric_tank}`}
      description={`${formatDateValue(toma.ric_date)} · registrada por ${toma.executor_nombre ?? 'usuario no identificado'}`}
      size="md"
    >
      <dl className="divide-y divide-ink-100">
        {filas.map((fila) => (
          <div key={fila.label} className="flex items-center justify-between gap-3 py-2">
            <dt className="min-w-0">
              <span className="text-sm text-ink-700">{fila.label}</span>
              {fila.nota ? (
                <span className="block text-[11px] text-ink-400">{fila.nota}</span>
              ) : null}
            </dt>
            <dd className="shrink-0 font-mono text-sm text-ink-900 tabular-nums">
              {formatNumber(fila.valor)}
            </dd>
          </div>
        ))}
      </dl>

      {fueraDeUmbral ? (
        <Alert variant="warning" title="Diferencia fuera del umbral" className="mt-4">
          La diferencia de {formatNumber(diferencia)} gal supera el umbral configurado de{' '}
          {formatNumber(umbral)} gal. Revisa la toma o los movimientos del período.
        </Alert>
      ) : null}
    </Dialog>
  );
}
